import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Hooks
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';

// Contexts
import TennisApiContext from '../contexts/TennisApiContext';

// Types
import {
  ITennisConfig,
  ITennisTournamentProgrammeResponseData,
  ITennisTournamentProgramme,
  ITennisCompetition,
} from '../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { FIXTURES, RESULTS } from '../../../../packages/constants/programmeStatus';
import dayjs from 'dayjs';
import { getValidatedDate } from '@sportal365/smp-widget-utils/src/date';

interface IUseTennisTournamentProgrammeArgs extends IGeneralSubscriberInfo {
  filter: string;
  dataRefreshTime?: ITennisConfig['dataRefreshTime'];
  dataLimit?: ITennisConfig['dataLimit'];
  dataDateFrom?: ITennisConfig['dataDateFrom'];
  dataDateTo?: ITennisConfig['dataDateTo'];
  dataTournament?: ITennisConfig['dataTournament'];
  seasonYear?: ITennisConfig['dataSeason'];
  dataCompetition?: ITennisConfig['dataCompetition'];
  dataRoundsFilter?: ITennisConfig['dataRoundsFilter'];
  dataOddsDisplay?: ITennisConfig['dataOddsDisplay'];
  dataOddsMarket: ITennisConfig['dataOddsMarket'];
  dataOddsMarketValueType?: ITennisConfig['dataOddsMarketValueType'];
  dataOddsBettingId?: ITennisConfig['dataOddsBettingId'];
  dataOddsPreEventOnly?: ITennisConfig['dataOddsPreEventOnly'];
  dataGameType?: ITennisConfig['dataGameType'];
  dataGender?: ITennisConfig['dataGender'];
  dataSortDirectionResults: ITennisConfig['dataSortDirectionResults'];
  dataSortDirectionFixtures: ITennisConfig['dataSortDirectionFixtures'];
}

export default function useTournamentProgramme({
  filter,
  subscribers,
  dataWidgetId,
  dataWidgetSport,
  dataRefreshTime,
  dataTournament,
  dataLimit,
  dataDateFrom,
  dataDateTo,
  seasonYear,
  dataCompetition,
  dataRoundsFilter,
  dataSortDirectionResults,
  dataSortDirectionFixtures,
  dataOddsDisplay,
  dataOddsMarket,
  dataOddsMarketValueType,
  dataOddsBettingId,
  dataOddsPreEventOnly,
  dataWidgetCuid,
  dataGameType = '',
  dataGender = '',
}: IUseTennisTournamentProgrammeArgs) {
  const { $tennis } = useContext(TennisApiContext);
  const [programme, setProgramme] = useState<ITennisTournamentProgramme[]>([]);
  const [competition, setCompetition] = useState<ITennisCompetition>();
  const [isLoading, setIsLoading] = useState<boolean>(!!dataCompetition);
  const [noUpcomingEvents, setNoUpcomingEvents] = useState(false);
  const [noPastEvents, setNoPastEvents] = useState(false);

  // Abort signal is not needed here because we need both requests with status types for the matches
  // Leaving it commented for reference
  // const [getSignal] = useAbortController();

  const startDate = useMemo(() => {
    return getValidatedDate(dayjs, dataDateFrom?.date, dataDateFrom?.dateFormat);
  }, [dataDateFrom?.date, dataDateFrom?.dateFormat]);

  const endDate = useMemo(() => {
    return getValidatedDate(dayjs, dataDateTo?.date, dataDateTo?.dateFormat);
  }, [dataDateTo?.date, dataDateTo?.dateFormat]);

  const fetchProgramme = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (dataCompetition) {
        // const signal = getSignal();
        manageLoader && setIsLoading(true);
        $tennis
          ?.getTournamentProgramme({
            filter,
            dataOddsMarket,
            dataOddsMarketValueType,
            dataOddsBettingId,
            dataTournament,
            seasonYear,
            dataCompetition,
            dataRoundsFilter,
            dataLimit,
            dataDateFrom: startDate,
            dataDateTo: endDate,
            dataSortDirectionResults,
            dataSortDirectionFixtures,
            dataOddsDisplay,
            dataOddsPreEventOnly,
            dataGameType,
            dataGender,
          })
          ?.then(({ programme, competition }: ITennisTournamentProgrammeResponseData) => {
            manageLoader && setIsLoading(false);

            if (programme?.length < 1 && filter === FIXTURES) {
              setNoUpcomingEvents(true);
            } else if (programme?.length < 1 && filter === RESULTS) {
              setNoPastEvents(true);
            } else if (programme?.length > 0 && filter === RESULTS) {
              setNoPastEvents(false);
            } else if (programme?.length > 0 && filter === FIXTURES) {
              setNoUpcomingEvents(false);
            }

            setProgramme(programme);
            setCompetition(competition);
            !isFromInterval && stackOdds(programme, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [
      $tennis,
      dataTournament,
      filter,
      dataLimit,
      startDate,
      endDate,
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsPreEventOnly,
      dataOddsMarket,
      dataOddsMarketValueType,
      seasonYear,
      dataCompetition,
      dataRoundsFilter,
      dataSortDirectionResults,
      dataSortDirectionFixtures,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
      dataGameType,
      dataGender,
    ],
  );

  const refetchWidgetData = useFetchIntervalData(fetchProgramme, dataRefreshTime);

  useEffect(() => {
    fetchProgramme(true);
  }, [fetchProgramme]);

  return {
    showWidget: true,
    widgetData: { programme, competition, isLoading, noUpcomingEvents, noPastEvents },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    refetchWidgetData,
  };
}
